import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Loader2, BedDouble, Bath, Car, ExternalLink, HelpCircle, UserCheck, Globe } from 'lucide-react';
import api from '../../../api';
import { formatMoney, preferenceParts } from '../utils/buyerPreferenceSummary';
import { OwnerContactList } from '../../dashboard/components/RelistingOwnerContacts';
import { MatchLevelChip, NextAction, ReasonChips, scoreTitle } from './MatchLevel';

/**
 * "Match properties" for one buyer (Christian Gravias, DOMA 13374913603):
 * every property in the CRM that fits what they want, grouped by Prospects
 * column plus Re-listing Opportunities. The fit is the property-alert matcher
 * (backend buyer_property_match), so these are the listings the buyer would be
 * alerted about, held to the buyer's exact budget.
 *
 * Each property carries the Property Match Score out of 100 (the same score
 * Find Buyers and Prospects Buyer Match show for the pair), its level, the
 * reasons behind it and the agent's next step, best match first within each
 * column. A fact the property has no data for is flagged to verify.
 *
 * Christian's follow-up (26 Oct 2026): Re-listing rows we hold owner details
 * for come first, Public Properties (the Database's owner rows) follow, and the
 * price is shown as the source gave it. A re-listing used to print the agency's
 * range and then our midpoint after it, which read as two different prices.
 */

const linkFor = (item) => {
  if (item.kind === 'property') return `/properties/${item.id}`;
  if (item.kind === 'lead') return `/prospects?stage=${item.stage}`;
  if (item.kind === 'public') return `/public-properties/${item.id}`;
  return '/prospecting/relisting-opportunities';
};

// A re-listing's guide is the agency's own wording ("$450,000 - $495,000");
// anything else is "<label> $price" (e.g. "Last sold $850k") or the price.
const priceText = (item) => {
  if (item.kind === 'relisting' && item.price_label) return item.price_label;
  if (item.price == null) return '';
  return `${item.price_label ? `${item.price_label} ` : ''}${formatMoney(item.price)}`;
};

/**
 * One matching property. A re-listing opens the listing itself, not the
 * Re-listing page, and its Owner details button shows everyone on file
 * (Christian Gravias, 26 Oct 2026: "Have a button where I can click to see the
 * owners details and a link to the property listing. If I click on the listing
 * currently it just sends me to the re-listing opportunities page").
 */
// Only a web address opens: listing_url comes from Homer's data, and an href
// must never carry a javascript: or data: URL.
const webUrl = (url) => {
  try {
    const parsed = new URL(url);
    return ['http:', 'https:'].includes(parsed.protocol) ? parsed.href : null;
  } catch {
    return null;
  }
};

const MatchCard = ({ item }) => {
  const [ownersOpen, setOwnersOpen] = useState(false);
  const listingHref = item.kind === 'relisting' ? webUrl(item.listing_url) : null;
  const owners = item.owner_contacts || [];
  const title = (
    <>
      {item.address || item.name}
      <ExternalLink className="inline w-3 h-3 ml-1 text-gray-400" />
    </>
  );
  const titleCls = 'font-semibold text-gray-900 hover:text-[#0e7490] hover:underline';
  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50/60 hover:bg-white hover:border-[#55C5D0]/50 hover:shadow-sm transition-all p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        {listingHref ? (
          <a href={listingHref} target="_blank" rel="noopener noreferrer" className={titleCls}>{title}</a>
        ) : (
          <Link to={linkFor(item)} target="_blank" rel="noopener noreferrer" className={titleCls}>{title}</Link>
        )}
        <span className="inline-flex items-baseline gap-3">
          {priceText(item) && (
            <span className="text-sm font-semibold text-gray-700">{priceText(item)}</span>
          )}
          {item.match_score != null && (
            <span className="text-sm font-bold text-[#0e7490]" title={scoreTitle(item.score_parts)}>
              {item.match_score} match
            </span>
          )}
          <MatchLevelChip match={item} />
        </span>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
        {item.kind !== 'property' && item.name && <span>{item.name}</span>}
        {item.property_type && <span className="capitalize">{item.property_type}</span>}
        {item.beds != null && <span className="inline-flex items-center gap-1"><BedDouble className="w-3.5 h-3.5" />{item.beds}</span>}
        {item.baths != null && <span className="inline-flex items-center gap-1"><Bath className="w-3.5 h-3.5" />{item.baths}</span>}
        {item.cars != null && <span className="inline-flex items-center gap-1"><Car className="w-3.5 h-3.5" />{item.cars}</span>}
        {item.agency && <span>{item.agency}</span>}
        {item.agent && <span>{item.agent}&apos;s Database</span>}
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        <ReasonChips match={item} />
        {owners.length > 0 ? (
          <button
            type="button"
            aria-expanded={ownersOpen}
            onClick={() => setOwnersOpen((o) => !o)}
            className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-semibold px-2 py-0.5 hover:bg-emerald-100"
          >
            <UserCheck className="w-3 h-3" /> {ownersOpen ? 'Hide owner details' : 'Owner details'}
          </button>
        ) : item.has_owner && (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-medium px-2 py-0.5"
            title="An owner is on file in the Database for this address.">
            <UserCheck className="w-3 h-3" /> Owner in Database
          </span>
        )}
        {item.details_unknown && (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 text-[11px] px-2 py-0.5"
            title="Some details are not on file for this property, so it matched on what is known.">
            <HelpCircle className="w-3 h-3" /> Matched on known details
          </span>
        )}
      </div>
      <NextAction match={item} className="mt-1.5" />
      {ownersOpen && (
        <div className="mt-2 rounded-lg border border-teal-100 bg-teal-50/40 px-3 py-2">
          <OwnerContactList contacts={owners} />
        </div>
      )}
    </div>
  );
};

const BuyerMatchPropertiesModal = ({ person, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('api/buyer-portal/analytics/find-buyers/match-properties/', {
          params: {
            ...(person.contact_id ? { contact_id: person.contact_id } : {}),
            ...(person.buyer_id ? { buyer_id: person.buyer_id } : {}),
          },
        });
        if (!cancelled) setData(res.data);
      } catch (error) {
        console.error('Failed to match properties:', error);
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [person.contact_id, person.buyer_id]);

  const groups = (data?.groups || []).filter((g) => g.items.length > 0);
  const prefs = preferenceParts(data?.preferences);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onMouseDown={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`Matching properties for ${person.name}`}
      >
        <div className="flex items-start justify-between gap-4 p-5 border-b border-gray-100">
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-gray-900">Matching properties · {person.name}</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {prefs.length ? `Wants ${prefs.join(' · ')}` : 'In our pipeline, Re-listing Opportunities and Public Properties.'}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : failed ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              We could not match properties just now. Close this and try again.
            </div>
          ) : !data?.preferences ? (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
              This buyer has no buying preferences saved yet, so there is nothing to match on.
            </div>
          ) : groups.length === 0 ? (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
              Nothing in the pipeline, Re-listing Opportunities or Public Properties fits this buyer right now.
            </div>
          ) : (
            <>
            {data.broad_city && (
              <div className="flex items-start gap-2 rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
                <Globe className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                <span>
                  This buyer named only {data.broad_city}, not a suburb, so these are broad matches on
                  budget, bedrooms and property type. Ask them which areas they like to narrow it down.
                </span>
              </div>
            )}
            {groups.map((group) => (
              <section key={group.key}>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                  {group.label} <span className="text-gray-400 font-normal">({group.items.length})</span>
                </h4>
                <div className="space-y-2">
                  {group.items.map((item) => (
                    <MatchCard key={`${item.kind}-${item.id}`} item={item} />
                  ))}
                </div>
              </section>
            ))}
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 p-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <span className="text-sm text-gray-500">
            {loading || failed ? '' : `${data?.total || 0} matching ${data?.total === 1 ? 'property' : 'properties'}`}
          </span>
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded-xl bg-gray-900 text-white text-sm font-medium hover:bg-gray-800">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BuyerMatchPropertiesModal;
